import { EChartsOption } from "echarts-for-react/lib/types"
import { cloneDeep } from "lodash-es"
import { action, makeAutoObservable, observable } from "mobx"
import numeral from "numeral"

import { SubscribeInfo } from "@/api/app/types/user"
import { IAppEmbedStatus, IIndexRecommend, TopData } from "@/api/dashboard/type"
import {
  IDashboardData,
  IViewsReturnRequestsData,
  ViewsReturnRequests,
  ViewsReturnRequestsChartOptionsData,
} from "@/models/IViewsReturnRequestsData"
import { DefaultChartOption } from "@/pages/Dashboard/components/Chart/DefaultChartOption"
import stores from "@/stores"
import dayjs from "@/utils/dayjs"
import { autoReplenishPoint } from "@/utils/formatNumber"

import { compareRateProps, IOperationMeta, legendDateProps } from "./type"

export default class ReturnRequestsDataStore implements IViewsReturnRequestsData {
  /** 数据加载完成时间 */
  @observable loadedDataTime: number = 0

  /* 绘制图表的配置 */
  @observable options: EChartsOption = DefaultChartOption

  /* 绘制图表列表数据 */
  @observable chartOptions = {
    returnsRequests: {
      ...DefaultChartOption,
    },
    returnsRate: {
      ...DefaultChartOption,
    },
    labelFee: {
      ...DefaultChartOption,
    },
    returnFee: {
      ...DefaultChartOption,
    },
    returnRewards: {
      ...DefaultChartOption,
    },
    shippingCost: {
      ...DefaultChartOption,
    },
    insuranceValue: {
      ...DefaultChartOption,
    },
  }

  /* 日期数组 */
  @observable date: string[][] = []

  @observable views_count: number = 0
  @observable requests_count: number = 0
  @observable count: Array<number> = []

  @observable back_in_store_value: number = 0

  @observable pending_review_count: number = 0
  @observable pending_refund_count: number = 0
  @observable pending_create_exchange_order_count: number = 0
  @observable pending_received_count: number = 0
  @observable pending_restock_count: number = 0

  @observable returns_value: number = 0
  @observable total_order_product_count: number = 0
  @observable total_order_return_count: number = 0
  @observable total_return_fee: number = 0
  @observable total_return_rate: number = 0
  @observable total_return_rewards: number = 0
  @observable total_label_fee: number = 0
  @observable total_ship_amount: number = 0
  @observable total_insurance_value: number = 0
  @observable exchange_upsell_value: number = 0
  @observable is_open_exchange_other: number = 0

  /** 总留存价值 */
  @observable total_retention_revenue: number = 0
  /** 非现金退款金额 */
  @observable total_no_cash: number = 0
  /** 换货率 */
  @observable total_exchange_rate: number = 0
  /** 现金退款率 */
  @observable total_refund_rate: number = 0
  /** 总留存价值环比变化百分比（正涨负跌） */
  @observable total_retention_revenue_compare: number = 0
  /** 商品退货率环比变化百分比（正涨负跌） */
  @observable total_return_rate_compare: number = 0

  /** app 集成情况（应用嵌入/应用块状态） */
  @observable app_embed_status: IAppEmbedStatus = {
    return_button_enabled: false,
    standalone_widget_enabled: false,
    return_protection_enabled: false,
  }

  /** 当前订阅套餐用量信息（index 接口返回，无套餐时缺失） */
  @observable subscribe_info?: SubscribeInfo

  /** 首页 Recommended for you 推荐项展示开关 */
  @observable index_recommend: IIndexRecommend = {
    exchange_open: false,
    shipping_method_open: false,
    start_recharge: false,
    work_flow_enable: false,
  }

  @observable show_praise_guide_modal: boolean = false
  @observable show_bad_feed_back_modal: boolean = false
  @observable hasCompare: boolean = true //  默认比较日期
  @observable operationMeta: IOperationMeta = {
    cur_period: "",
    day: 30,
    has_custom_date_filter: false,
    pre_period: "",
  }

  @observable loading = {
    dashboardLoading: false,
    overviewLoading: false,
    operationLoading: false,
  }

  @observable analyticsType = 0

  @observable analyzeTop: TopData = {
    return_reasons: {
      cur: [],
      pre: [],
    },
    return_variants: {
      cur: [],
      pre: [],
    },
    return_solutions: {
      cur: [],
      pre: [],
    },
    return_countries: {
      cur: [],
      pre: [],
    },
  }

  // 环比数据
  @observable compare_rate: compareRateProps = {
    request_count: 0,
    order_product_count: 0,
    order_return_count: 0,
    return_rate: 0,
    label_fee: 0,
    return_fee: 0,
    return_rewards: 0,
    total_ship_amount: 0,
    total_insurance_value: 0,
  }

  // 日期
  @observable legendDate: legendDateProps = {
    date: "",
    compareDate: "",
  }

  // 分析表格日期时间记录
  @observable analyzeTableDate: any = {
    day: 30,
    subscribed_at_min: undefined,
    subscribed_at_max: undefined,
  }

  constructor() {
    makeAutoObservable(this)
  }

  @action.bound
  setAnalyzeTop(data: TopData) {
    this.analyzeTop = data
  }
  @action.bound
  setLoading(key: string, value: boolean) {
    this.loading[key] = value
  }
  @action.bound
  setAnalyticsType(value: number) {
    this.analyticsType = value
  }

  @action.bound
  setShowPraiseGuideModal(show: boolean) {
    this.show_praise_guide_modal = show
  }
  @action.bound
  setShowBadFeedBackModal(show: boolean) {
    this.show_bad_feed_back_modal = show
  }
  @action.bound
  setHasCompare(show: boolean) {
    this.hasCompare = show
  }
  @action.bound
  setOperationMeta(meta: IOperationMeta) {
    this.operationMeta = meta
  }

  // 处理首页统计图数据
  hanldeStatisticsData = (data) => {
    const xAxis: ViewsReturnRequests["date"] = []

    const yAxisFields = [
      { key: "returnsRequests", field: "request_count" },
      { key: "returnsRate", field: "return_rate" },
      { key: "labelFee", field: "label_fee" },
      { key: "returnFee", field: "return_fee" },
      { key: "returnRewards", field: "return_rewards" },
      { key: "shippingCost", field: "total_ship_amount" },
      { key: "insuranceValue", field: "insurance_value" },
    ]

    const yAxis = yAxisFields.reduce((acc, { key }) => {
      acc[key] = { current: [], previous: [] }
      return acc
    }, {})

    data.forEach((item) => {
      xAxis.push(dayjs(item.date, "YYYY-MM-DD").format("MMM D"))

      yAxisFields.forEach(({ key, field }) => {
        const _current = {
          value: item.current[field] ? item.current[field] : 0.0,
          name: item.current.date,
        }
        const _previous = {
          value: item.previous[field] ? item.previous[field] : 0.0,
          name: item.previous.date,
        }
        yAxis[key].current.push(_current)
        yAxis[key].previous.push(_previous)
      })
    })

    return { xAxis, yAxis }
  }

  /**
   * 将从 API 获取的数据复制给声明变量
   * @param data
   * @param legendData
   */
  @action.bound
  setData(resData: ViewsReturnRequests, legendData: string[]): void {
    let currentKeys: keyof ViewsReturnRequests

    // 先提取 hasCompare，避免在循环中被错误类型覆盖
    const { hasCompare, ...restResData } = resData

    for (currentKeys in restResData) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this[currentKeys] = restResData[currentKeys]
    }
    // 单独处理 hasCompare，确保类型正确
    if (hasCompare !== undefined) {
      this.hasCompare = hasCompare
    }
    const {
      data,
      back_in_store_value,
      pending_review_count,
      returns_value,
      pending_refund_count,
      pending_create_exchange_order_count,
      pending_received_count,
      pending_restock_count,
      exchange_upsell_value,
      is_open_exchange_other,
    } = resData
    const { xAxis, yAxis } = this.hanldeStatisticsData(data)
    // console.log("xAxis", xAxis)
    // console.log("yAxis", yAxis)

    this.setOptionsData({ xAxis, yAxis, hasCompare })
    this.setLegendData(legendData)
    this.setDisplayInfo(
      back_in_store_value,
      pending_review_count,
      returns_value,
      pending_refund_count,
      pending_create_exchange_order_count,
      pending_received_count,
      pending_restock_count,
      exchange_upsell_value,
      is_open_exchange_other,
    )
    this.setLoading("overviewLoading", false)
  }

  setDashboardData(resData: IDashboardData): void {
    let currentKeys: keyof ViewsReturnRequests

    // 先提取 hasCompare，避免在循环中被错误类型覆盖
    const { hasCompare, ...restResData } = resData

    for (currentKeys in restResData) {
      this[currentKeys] = restResData[currentKeys]
    }
    // 单独处理 hasCompare，确保类型正确
    if (hasCompare !== undefined) {
      this.hasCompare = hasCompare
    }
    this.setLoading("dashboardLoading", false)
  }

  @action.bound
  setDisplayInfo(
    back_in_store_value: number,
    pending_review_count: number,
    returns_value: number,
    pending_refund_count: number,
    pending_create_exchange_order_count: number,
    pending_received_count: number,
    pending_restock_count: number,
    exchange_upsell_value: number,
    is_open_exchange_other: number,
  ) {
    this.back_in_store_value = back_in_store_value
    this.pending_review_count = pending_review_count
    this.returns_value = returns_value
    this.pending_refund_count = pending_refund_count
    this.pending_create_exchange_order_count =
      pending_create_exchange_order_count
    this.pending_received_count = pending_received_count
    this.pending_restock_count = pending_restock_count
    this.exchange_upsell_value = exchange_upsell_value
    this.is_open_exchange_other = is_open_exchange_other
  }

  chartOptionsMap = {
    returnsRequests: "Returns requests",
    returnsRate: "Returns rate",
    labelFee: "Shipping fee",
    returnFee: "Restocking fee",
    returnRewards: "Return rewards",
    shippingCost: "Shipping cost",
  }

  /**
   * 设置图表数据
   * @param params
   */
  @action.bound
  setOptionsData(params: ViewsReturnRequestsChartOptionsData): void {
    const { UserInfoStore } = stores
    // 这里做遍历数据处理 这里对应tab的options数据处理
    // 设置图表默认值
    const newOption = cloneDeep(this.chartOptions)
    const legendDate = this.operationMeta
    const { xAxis, yAxis = {}, hasCompare } = params || {}
    Object.keys(yAxis || {})?.forEach((key) => {
      // 设置X轴值
      if (xAxis) {
        newOption[key].xAxis[0].data = xAxis
        // 设置 X 轴文字颜色
        newOption[key].xAxis[0].axisLabel.color = "#616161"
      }
      // 设置 Y 轴文字颜色
      newOption[key].yAxis[0].axisLabel.color = "#616161"

      // 设置 Y 轴分割线颜色
      newOption[key].yAxis[0].splitLine = {
        show: true,
        lineStyle: {
          color: "#EEEEEF",
          type: "solid", // 实线，可选 "dashed" | "dotted"
          width: 1,
        },
      }
      // views 数据 (平滑、颜色、背景颜色)
      if (Array.isArray(yAxis[key]?.current) && yAxis[key]?.current.length) {
        newOption[key].series[0].data = yAxis[key]?.current
        newOption[key].series[0].smooth = true
        newOption[key].series[0].areaStyle = null
        newOption[key].series[0].itemStyle = {
          color: "#35B2EA",
        }
        newOption[key].series[0].name = legendDate.cur_period

        // 判断 hasCompare  展示 series[1] 比较的数据
        if (hasCompare) {
          newOption[key].series[1].data = yAxis[key]?.previous
          newOption[key].series[1].smooth = true
          newOption[key].series[1].itemStyle = {
            color: "#35B2EA",
          }
          newOption[key].series[1].name = legendDate.pre_period
          // 图例包含比较日期
          newOption[key].legend.data = [
            { name: legendDate.cur_period, icon: "roundRect" },
            {
              name: legendDate.pre_period,
              icon: "path://M0 5 A1 1 0 1 1 2 5 M3 5 A1 1 0 1 1 5 5 M6 5 A1 1 0 1 1 8 5", // 三个圆点
            },
          ]
        } else {
          // 不展示对比数据时，仅有主数据和主数据图例
          newOption[key].series[1].data = []
          newOption[key].legend.data = [
            { name: legendDate.cur_period, icon: "roundRect" },
          ]
        }

        newOption[key].legend.textStyle = {
          color: "#616161",
        }
        // 禁用图例交互，图例仅作为展示
        newOption[key].legend.selectedMode = false

        // 退货率百分比展示处理
        if (key === "returnsRate") {
          // Y轴百分比处理
          newOption[key].yAxis[0].axisLabel = {
            color: "#637381",
            fontSize: "12",
            formatter: "{value}%",
          }
          // 鼠标hover展示百分比处理
          newOption[key].tooltip.formatter = function (params) {
            let result = ``
            params.forEach(function (item) {
              const dataPoint = item.data
              let iconStyle = ""
              // 根据折线的名称设置不同的图标样式
              if (item.seriesName === legendDate.cur_period) {
                iconStyle = `width: 12px; height: 2px; background-color: ${item.color}; border-radius: 3px;`
              } else if (item.seriesName === legendDate.pre_period) {
                iconStyle = `width: 12px; height: 2px; border: 1px dashed ${item.color}; border-radius: 3px;`
              }
              result += `
                    <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px;">
                        <div style="display: flex; align-items: center">
                          <span style="display: inline-block; ${iconStyle}; margin-right: 10px;"></span>
                          <span style="color: #616161;">${dataPoint.name}: </span>
                        </div>
                        <span style="font-weight: bold;">${autoReplenishPoint(numeral(dataPoint.value).format("0, 0.00"))}%</span>
                    </div>`
            })
            return result
          }
        } else {
          // hover效果展示默认填充保留两位小数
          newOption[key].tooltip.formatter = function (params) {
            let result = ``
            params.forEach(function (item) {
              const dataPoint = item.data
              let iconStyle = ""
              // 根据折线的名称设置不同的图标样式
              if (item.seriesName === legendDate.cur_period) {
                iconStyle = `width: 12px; height: 2px; background-color: ${item.color}; border-radius: 3px;`
              } else if (item.seriesName === legendDate.pre_period) {
                iconStyle = `width: 12px; height: 2px; border: 1px dashed ${item.color}; border-radius: 3px;`
              }
              result += `
                    <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px;">
                        <div style="display: flex; align-items: center">
                          <span style="display: inline-block; ${iconStyle}; margin-right: 10px;"></span>
                          <span style="color: #616161;">${dataPoint.name}: </span>
                        </div>
                        <span style="font-weight: bold">${
                          key === "returnsRequests"
                            ? item.value
                            : `${UserInfoStore.data.currency_format}${autoReplenishPoint(numeral(item.value).format("0, 0.00"))}`
                        }</span>
                    </div>`
            })
            return result
          }
        }
      }
    })

    // 最终赋值数据
    this.chartOptions = newOption
  }

  /**
   * 设置数据加载完成时间
   * @param time
   */
  @action.bound
  setLoadedDataTime(time: number): void {
    this.loadedDataTime = time
  }

  @action.bound
  setLegendData(data: string[]): void {
    const options = cloneDeep(this.options)

    // eslint-disable-next-line prefer-destructuring
    options.series[0].name = data[0]

    // eslint-disable-next-line prefer-destructuring
    options.legend.data[0] = data[0]

    this.options = options
  }

  /**
   * 设置环比率数据
   * @param time
   */
  @action.bound
  setCompareRate(data: compareRateProps) {
    this.compare_rate = data
  }

  /**
   * 设置环比日期值
   * @param time
   */

  @action.bound
  setLegendDate(data) {
    this.legendDate = data
  }

  @action.bound
  setAnalyzeTableDate(info: {
    day: number
    subscribed_at_min?: string
    subscribed_at_max?: string
  }) {
    this.analyzeTableDate = info
  }

  @action.bound
  setShowIndexRecommend(show: boolean) {
    this.index_recommend.show_index_recommend = show
  }
}
